import React, { useState, useEffect, useMemo } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  Modal,
  ModalBody,
  DropdownItem,
  Form,
  Badge,
  Input,
  Label,
  Spinner,
} from "reactstrap";
import {
  Block,
  BlockHead,
  BlockBetween,
  BlockHeadContent,
  BlockTitle,
  BlockDes,
  Icon,
  Button,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  PaginationComponent,
  RSelect,
  PreviewCard,
  PreviewAltCard,
} from "../../../components/Component";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";
import { Row, Col } from "reactstrap";

const WORKFLOW_TRANSITIONS = {
  submitted: ["received"],
  received: ["in_analysis"],
  in_analysis: ["corrective_action_requested"],
  corrective_action_requested: ["action_completed"],
  action_completed: ["verified"],
  verified: ["closed"],
  closed: [],
};

const STATUS_OPTIONS = [
  { value: "submitted", label: "Soumise" },
  { value: "received", label: "Reçue" },
  { value: "in_analysis", label: "En analyse" },
  { value: "corrective_action_requested", label: "Action requise" },
  { value: "action_completed", label: "Action réalisée" },
  { value: "verified", label: "Vérifiée" },
  { value: "closed", label: "Clôturée" },
];

const getErrorMessage = (error, fallback) => {
  if (error?.response?.data?.errors) {
    const messages = Object.values(error.response.data.errors).flat();
    if (messages.length) return messages.join(", ");
  }
  return error?.response?.data?.message || error?.message || fallback;
};

const WorkflowListPage = () => {
  const [sm, updateSm] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: "all", type: "all", severity: "all" });
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage] = useState(10);
  const [selectedItems, setSelectedItems] = useState([]);
  const [bulkActionModal, setBulkActionModal] = useState(false);
  const [detailSidebar, setDetailSidebar] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Modals
  const [statusModal, setStatusModal] = useState(false);
  const [historyModal, setHistoryModal] = useState(false);
  const [assignModal, setAssignModal] = useState(false);
  const [selectedObservation, setSelectedObservation] = useState(null);
  const [workflowHistory, setWorkflowHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [users, setUsers] = useState([]);

  // Form data for status update
  const [statusFormData, setStatusFormData] = useState({
    status: "",
    action: "",
    comments: "",
    due_date: "",
    assigned_to: "",
  });

  useEffect(() => {
    loadWorkflow();
  }, [filters]);

  useEffect(() => {
    loadUsers();
  }, []);

  const userSelectOptions = useMemo(
    () =>
      users.map((u) => {
        const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
        const label = name ? `${name} (${u.email})` : u.email;
        return { value: u.id, label };
      }),
    [users]
  );

  const loadWorkflow = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status && filters.status !== "all") {
        params.status = filters.status;
      }
      const response = await api.getObservations(params);
      setData(response.data || []);
    } catch (error) {
      toast.error("Erreur lors du chargement du workflow");
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.getUsers({ per_page: 500, status: "active" });
      const list = response?.data ?? response ?? [];
      setUsers(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Erreur lors du chargement des utilisateurs:", error);
      toast.error("Impossible de charger la liste des utilisateurs");
      setUsers([]);
    }
  };

  const selectedAssigneeOption = (userId) => {
    if (userId === "" || userId === null || userId === undefined) return null;
    const id = typeof userId === "string" ? Number(userId) : userId;
    return userSelectOptions.find((o) => o.value === id) || null;
  };

  const loadWorkflowHistory = async (observationId) => {
    setLoadingHistory(true);
    try {
      const response = await api.getWorkflowHistory(observationId);
      setWorkflowHistory(response || []);
    } catch (error) {
      toast.error("Erreur lors du chargement de l'historique");
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleStatusChange = async () => {
    if (!selectedObservation || !statusFormData.status) {
      toast.error("Veuillez sélectionner un statut");
      return;
    }

    try {
      await api.updateObservationStatus(selectedObservation.id, statusFormData.status, {
        action: statusFormData.action,
        comments: statusFormData.comments,
        due_date: statusFormData.due_date,
        ...(statusFormData.assigned_to !== "" &&
          statusFormData.assigned_to !== null &&
          statusFormData.assigned_to !== undefined && {
            assigned_to: Number(statusFormData.assigned_to),
          }),
      });
      toast.success("Statut mis à jour avec succès");
      setStatusModal(false);
      setStatusFormData({
        status: "",
        action: "",
        comments: "",
        due_date: "",
        assigned_to: "",
      });
      loadWorkflow();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors de la mise à jour du statut"));
    }
  };

  const openStatusModal = (observation) => {
    const allowedNext = WORKFLOW_TRANSITIONS[observation.status] || [];
    setSelectedObservation(observation);
    setStatusFormData({
      status: allowedNext[0] || "",
      action: "",
      comments: "",
      due_date: "",
      assigned_to: observation.assigned_to?.id ?? "",
    });
    setStatusModal(true);
    loadUsers();
  };

  const openHistoryModal = async (observation) => {
    setSelectedObservation(observation);
    setHistoryModal(true);
    await loadWorkflowHistory(observation.id);
  };

  const openAssignModal = (observation) => {
    setSelectedObservation(observation);
    setStatusFormData({
      status: observation.status || "",
      action: "",
      comments: "",
      due_date: "",
      assigned_to: observation.assigned_to?.id ?? "",
    });
    setAssignModal(true);
    loadUsers();
  };

  const handleAssign = async () => {
    if (!selectedObservation || !statusFormData.assigned_to) {
      toast.error("Veuillez sélectionner un utilisateur");
      return;
    }

    try {
      await api.updateObservationStatus(selectedObservation.id, selectedObservation.status, {
        assigned_to: Number(statusFormData.assigned_to),
      });
      toast.success("Observation assignée avec succès");
      setAssignModal(false);
      loadWorkflow();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors de l'assignation"));
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      submitted: { color: "secondary", label: "Soumise" },
      received: { color: "info", label: "Reçue" },
      in_analysis: { color: "warning", label: "En analyse" },
      corrective_action_requested: { color: "danger", label: "Action requise" },
      action_completed: { color: "success", label: "Action réalisée" },
      verified: { color: "primary", label: "Vérifiée" },
      closed: { color: "dark", label: "Clôturée" },
    };
    const badge = badges[status] || { color: "secondary", label: status };
    return <Badge color={badge.color}>{badge.label}</Badge>;
  };

  const getTypeBadge = (type) => {
    const badges = {
      positive: { color: "success", label: "Positive" },
      dangerous: { color: "danger", label: "Dangereuse" },
      near_miss: { color: "warning", label: "Presqu'accident" },
      non_conformity: { color: "info", label: "Non-conformité" },
    };
    const badge = badges[type] || { color: "secondary", label: type };
    return <Badge color={badge.color}>{badge.label}</Badge>;
  };

  // Filtrer et trier les données avec useMemo
  const filteredData = useMemo(() => {
    let filtered = data.filter((item) => {
      const matchesSearch =
        !searchText ||
        item.title?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchText.toLowerCase());
      const matchesStatus = !filters.status || filters.status === "all" || item.status === filters.status;
      const matchesType = !filters.type || filters.type === "all" || item.type === filters.type;
      const matchesSeverity = !filters.severity || filters.severity === "all" || item.severity === filters.severity;
      return matchesSearch && matchesStatus && matchesType && matchesSeverity;
    });

    // Trier les données
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === "created_at") {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        } else if (typeof aValue === "string") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [data, searchText, filters, sortConfig]);

  // Calculer les statistiques après avoir défini filteredData
  const stats = useMemo(() => ({
    total: filteredData.length,
    submitted: filteredData.filter((item) => item.status === "submitted").length,
    inAnalysis: filteredData.filter((item) => item.status === "in_analysis").length,
    actionRequired: filteredData.filter((item) => item.status === "corrective_action_requested").length,
    urgent: filteredData.filter((item) => {
      const isHighSeverity = item.severity === "high";
      const isActionRequired = item.status === "corrective_action_requested";
      return isHighSeverity || isActionRequired;
    }).length,
  }), [filteredData]);

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc",
    });
  };

  const indexOfLastItem = currentPage * itemPerPage;
  const indexOfFirstItem = indexOfLastItem - itemPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const statusOptions = STATUS_OPTIONS;
  const allowedStatusOptions = selectedObservation
    ? statusOptions.filter((opt) => (WORKFLOW_TRANSITIONS[selectedObservation.status] || []).includes(opt.value))
    : statusOptions;

  // Actions en masse
  const handleBulkStatusChange = async () => {
    if (selectedItems.length === 0) {
      toast.error("Veuillez sélectionner au moins une observation");
      return;
    }

    if (!statusFormData.status) {
      toast.error("Veuillez sélectionner un statut");
      return;
    }

    try {
      const promises = selectedItems.map((id) =>
        api.updateObservationStatus(id, statusFormData.status, {
          comments: `Mise à jour en masse: ${statusFormData.comments || "Aucun commentaire"}`,
        })
      );
      await Promise.all(promises);
      toast.success(`${selectedItems.length} observation(s) mise(s) à jour avec succès`);
      setBulkActionModal(false);
      setSelectedItems([]);
      setStatusFormData({ ...statusFormData, status: "", comments: "" });
      loadWorkflow();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors de la mise à jour en masse"));
    }
  };


  // Sélection multiple
  const toggleItemSelection = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === currentItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(currentItems.map((item) => item.id));
    }
  };

  return (
    <React.Fragment>
      <Head title="Workflow HSE" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Workflow HSE</BlockTitle>
              <BlockDes className="text-soft">
                {filteredData.length} observation(s) au total
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <Button
                  className={`btn-icon btn-trigger toggle-expand me-n1 ${sm ? "active" : ""}`}
                  onClick={() => updateSm(!sm)}
                >
                  <Icon name="menu-alt-r"></Icon>
                </Button>
                <div className="toggle-expand-content" style={{ display: sm ? "block" : "none" }}>
                  <ul className="nk-block-tools g-3">
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        {/* Statistiques rapides */}
        <Block>
          <Row className="g-gs">
            <Col sm="6" lg="3">
              <PreviewAltCard style={{ padding: "0.5rem 1rem", minHeight: "auto" }}>
                <div className="card-title-group align-start" style={{ marginBottom: "0.25rem", paddingBottom: 0 }}>
                  <div className="card-title">
                    <h6 className="title">Total</h6>
                    <p>Observations</p>
                  </div>
                </div>
                <div className="amount" style={{ marginTop: "0.25rem" }}>{stats.total}</div>
              </PreviewAltCard>
            </Col>
            <Col sm="6" lg="3">
              <PreviewAltCard style={{ padding: "0.5rem 1rem", minHeight: "auto" }}>
                <div className="card-title-group align-start" style={{ marginBottom: "0.25rem", paddingBottom: 0 }}>
                  <div className="card-title">
                    <h6 className="title">Soumises</h6>
                    <p>En attente</p>
                  </div>
                </div>
                <div className="amount text-warning" style={{ marginTop: "0.25rem" }}>{stats.submitted}</div>
              </PreviewAltCard>
            </Col>
            <Col sm="6" lg="3">
              <PreviewAltCard style={{ padding: "0.5rem 1rem", minHeight: "auto" }}>
                <div className="card-title-group align-start" style={{ marginBottom: "0.25rem", paddingBottom: 0 }}>
                  <div className="card-title">
                    <h6 className="title">En analyse</h6>
                    <p>En cours</p>
                  </div>
                </div>
                <div className="amount text-info" style={{ marginTop: "0.25rem" }}>{stats.inAnalysis}</div>
              </PreviewAltCard>
            </Col>
            <Col sm="6" lg="3">
              <PreviewAltCard style={{ padding: "0.5rem 1rem", minHeight: "auto" }}>
                <div className="card-title-group align-start" style={{ marginBottom: "0.25rem", paddingBottom: 0 }}>
                  <div className="card-title">
                    <h6 className="title">Action requise</h6>
                    <p>Urgent</p>
                  </div>
                </div>
                <div className="amount text-danger" style={{ marginTop: "0.25rem" }}>{stats.actionRequired}</div>
              </PreviewAltCard>
            </Col>
          </Row>
        </Block>

        <Block>
          <div className="mb-3">
            <Row className="g-2 align-items-end">
              <Col md="4">
                <Label>Recherche</Label>
                <div className="form-control-wrap">
                  <div className="form-icon form-icon-left">
                    <Icon name="search"></Icon>
                  </div>
                  <Input
                    type="text"
                    className="form-control"
                    placeholder="Rechercher une observation..."
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </Col>
              <Col md="3">
                <Label>Type</Label>
                <RSelect
                  options={[
                    { value: "all", label: "Tous" },
                    { value: "positive", label: "Positive" },
                    { value: "dangerous", label: "Dangereuse" },
                    { value: "near_miss", label: "Presqu'accident" },
                    { value: "non_conformity", label: "Non-conformité" },
                  ]}
                  value={{ value: filters.type || "all", label: filters.type === "all" ? "Tous" : filters.type }}
                  onChange={(e) => setFilters({ ...filters, type: e.value })}
                />
              </Col>
              <Col md="3">
                <Label>Gravité</Label>
                <RSelect
                  options={[
                    { value: "all", label: "Toutes" },
                    { value: "low", label: "Faible" },
                    { value: "medium", label: "Moyenne" },
                    { value: "high", label: "Élevée" },
                  ]}
                  value={{ value: filters.severity || "all", label: filters.severity === "all" ? "Toutes" : filters.severity }}
                  onChange={(e) => setFilters({ ...filters, severity: e.value })}
                />
              </Col>
              <Col md="2">
                <div className="d-flex g-2 justify-content-end">
                  {selectedItems.length > 0 && (
                    <Button
                      color="primary"
                      onClick={() => setBulkActionModal(true)}
                      className="me-2"
                      size="sm"
                    >
                      <Icon name="check-round"></Icon>
                      <span>Actions ({selectedItems.length})</span>
                    </Button>
                  )}
                </div>
              </Col>
            </Row>
          </div>

          {loading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : filteredData.length === 0 ? (
            <PreviewCard>
              <div className="text-center py-4">
                <span className="text-soft">Aucune observation trouvée</span>
              </div>
            </PreviewCard>
          ) : (
            <DataTable className="card-stretch workflow-hse-table">
              <DataTableBody>
                <DataTableHead className="nk-tb-item nk-tb-head">
                  <DataTableRow className="nk-tb-col-check">
                    <div className="custom-control custom-control-sm custom-checkbox notext">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="wf-all"
                        checked={selectedItems.length === currentItems.length && currentItems.length > 0}
                        onChange={toggleSelectAll}
                      />
                      <label className="custom-control-label" htmlFor="wf-all"></label>
                    </div>
                  </DataTableRow>
                  <DataTableRow>
                    <span className="sub-text">Observation</span>
                  </DataTableRow>
                  <DataTableRow size="xl">
                    <span className="sub-text">Type</span>
                  </DataTableRow>
                  <DataTableRow size="xl">
                    <span className="sub-text">Statut</span>
                  </DataTableRow>
                  <DataTableRow size="xxl">
                    <span className="sub-text">Assigné à</span>
                  </DataTableRow>
                  <DataTableRow size="md">
                    <span className="sub-text">Date</span>
                  </DataTableRow>
                  <DataTableRow className="nk-tb-col-tools text-end">
                    <span className="sub-text">Actions</span>
                  </DataTableRow>
                </DataTableHead>
                {currentItems.map((item) => (
                  <DataTableItem key={item.id}>
                    <DataTableRow className="nk-tb-col-check">
                      <div className="custom-control custom-control-sm custom-checkbox notext">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id={`wf-${item.id}`}
                          checked={selectedItems.includes(item.id)}
                          onChange={() => toggleItemSelection(item.id)}
                        />
                        <label className="custom-control-label" htmlFor={`wf-${item.id}`}></label>
                      </div>
                    </DataTableRow>
                    <DataTableRow>
                      <div
                        onClick={(ev) => {
                          ev.preventDefault();
                          setSelectedObservation(item);
                          setDetailSidebar(true);
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        <h6 className="title" style={{ maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: "0.125rem" }} title={item.title}>
                          {item.title || "Sans titre"}
                          {(item.severity === "high" || item.status === "corrective_action_requested") && (
                            <Badge color="danger" className="ms-2">Urgent</Badge>
                          )}
                        </h6>
                        <span className="sub-text">
                          {item.description?.substring(0, 50) || ""}
                          {item.description?.length > 50 ? "..." : ""}
                        </span>
                      </div>
                    </DataTableRow>
                    <DataTableRow size="xl">
                      {getTypeBadge(item.type)}
                    </DataTableRow>
                    <DataTableRow size="xl">
                      {getStatusBadge(item.status)}
                    </DataTableRow>
                    <DataTableRow size="xxl">
                      <span>
                        {item.assignee
                          ? `${item.assignee.first_name || ""} ${item.assignee.last_name || ""}`.trim() || item.assignee.email
                          : "Non assigné"}
                      </span>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <span style={{ whiteSpace: "nowrap" }}>
                        {item.created_at ? new Date(item.created_at).toLocaleDateString("fr-FR") : "-"}
                      </span>
                    </DataTableRow>
                    <DataTableRow className="nk-tb-col-tools text-end">
                      <ul className="nk-tb-actions gx-1">
                        <li>
                          <UncontrolledDropdown>
                            <DropdownToggle tag="a" className="text-soft dropdown-toggle btn btn-icon btn-trigger">
                              <Icon name="more-h"></Icon>
                            </DropdownToggle>
                            <DropdownMenu end>
                              <ul className="link-list-opt no-bdr">
                                <li>
                                  <DropdownItem
                                    tag="a"
                                    href="#status"
                                    onClick={(ev) => {
                                      ev.preventDefault();
                                      openStatusModal(item);
                                    }}
                                  >
                                    <Icon name="edit"></Icon>
                                    <span>Changer le statut</span>
                                  </DropdownItem>
                                </li>
                                <li>
                                  <DropdownItem
                                    tag="a"
                                    href="#assign"
                                    onClick={(ev) => {
                                      ev.preventDefault();
                                      openAssignModal(item);
                                    }}
                                  >
                                    <Icon name="user"></Icon>
                                    <span>Assigner</span>
                                  </DropdownItem>
                                </li>
                                <li>
                                  <DropdownItem
                                    tag="a"
                                    href="#history"
                                    onClick={(ev) => {
                                      ev.preventDefault();
                                      openHistoryModal(item);
                                    }}
                                  >
                                    <Icon name="history"></Icon>
                                    <span>Historique</span>
                                  </DropdownItem>
                                </li>
                              </ul>
                            </DropdownMenu>
                          </UncontrolledDropdown>
                        </li>
                      </ul>
                    </DataTableRow>
                  </DataTableItem>
                ))}
              </DataTableBody>
              <div className="card-inner">
                {filteredData.length > 0 && (
                  <PaginationComponent
                    itemPerPage={itemPerPage}
                    totalItems={filteredData.length}
                    paginate={paginate}
                    currentPage={currentPage}
                  />
                )}
              </div>
            </DataTable>
          )}
        </Block>

        {/* Modal pour changer le statut */}
        <Modal isOpen={statusModal} toggle={() => setStatusModal(false)} className="modal-dialog-centered">
          <div className="modal-header">
            <h5 className="modal-title">Changer le statut</h5>
            <button className="close" onClick={() => setStatusModal(false)}>
              <Icon name="cross"></Icon>
            </button>
          </div>
          <ModalBody>
            {selectedObservation && (
              <div>
                <div className="form-group mb-3">
                  <Label>Observation</Label>
                  <div className="form-control-plaintext">{selectedObservation.title}</div>
                </div>
                <div className="form-group mb-3">
                  <Label>Nouveau statut <span className="text-danger">*</span></Label>
                  <RSelect
                    options={allowedStatusOptions}
                    value={allowedStatusOptions.find((opt) => opt.value === statusFormData.status) || null}
                    onChange={(e) => setStatusFormData({ ...statusFormData, status: e.value })}
                    placeholder={allowedStatusOptions.length ? "Sélectionner l'étape suivante..." : "Aucune transition possible"}
                    isDisabled={!allowedStatusOptions.length}
                  />
                  <small className="text-soft">
                    Transitions autorisées: {allowedStatusOptions.length ? allowedStatusOptions.map((s) => s.label).join(" -> ") : "aucune (observation déjà clôturée)"}
                  </small>
                </div>
                <div className="form-group mb-3">
                  <Label>Action</Label>
                  <Input
                    type="textarea"
                    rows="3"
                    value={statusFormData.action}
                    onChange={(e) => setStatusFormData({ ...statusFormData, action: e.target.value })}
                    placeholder="Décrivez l'action à entreprendre..."
                  />
                </div>
                <div className="form-group mb-3">
                  <Label>Commentaires</Label>
                  <Input
                    type="textarea"
                    rows="3"
                    value={statusFormData.comments}
                    onChange={(e) => setStatusFormData({ ...statusFormData, comments: e.target.value })}
                    placeholder="Ajoutez des commentaires..."
                  />
                </div>
                <div className="form-group mb-3">
                  <Label>Date d'échéance</Label>
                  <Input
                    type="date"
                    value={statusFormData.due_date}
                    onChange={(e) => setStatusFormData({ ...statusFormData, due_date: e.target.value })}
                  />
                </div>
                <div className="form-group mb-3">
                  <Label>Assigner à</Label>
                  <RSelect
                    options={userSelectOptions}
                    value={selectedAssigneeOption(statusFormData.assigned_to)}
                    onChange={(opt) =>
                      setStatusFormData({
                        ...statusFormData,
                        assigned_to: opt ? opt.value : "",
                      })
                    }
                    placeholder="Sélectionner un utilisateur…"
                    isClearable
                    isSearchable
                  />
                  <small className="text-soft">Optionnel — laissez vide pour ne pas modifier l&apos;assignation</small>
                </div>
                <div className="text-end mt-4">
                  <Button color="light" onClick={() => setStatusModal(false)} className="me-2">
                    Annuler
                  </Button>
                  <Button color="primary" onClick={handleStatusChange} disabled={!allowedStatusOptions.length}>
                    Mettre à jour
                  </Button>
                </div>
              </div>
            )}
          </ModalBody>
        </Modal>

        {/* Modal pour l'historique */}
        <Modal isOpen={historyModal} toggle={() => setHistoryModal(false)} className="modal-dialog-centered modal-lg">
          <div className="modal-header">
            <h5 className="modal-title">Historique du workflow</h5>
            <button className="close" onClick={() => setHistoryModal(false)}>
              <Icon name="cross"></Icon>
            </button>
          </div>
          <ModalBody>
            {selectedObservation && (
              <div>
                <div className="form-group mb-3">
                  <Label>Observation</Label>
                  <div className="form-control-plaintext">{selectedObservation.title}</div>
                </div>
                {loadingHistory ? (
                  <div className="text-center py-4">
                    <Spinner color="primary" />
                  </div>
                ) : workflowHistory.length > 0 ? (
                  <div className="timeline">
                    {workflowHistory.map((action, index) => (
                      <div key={action.id || index} className="timeline-item">
                        <div className="timeline-time">
                          {action.created_at
                            ? new Date(action.created_at).toLocaleString("fr-FR")
                            : "-"}
                        </div>
                        <div className="timeline-badge">
                          <Icon name="check"></Icon>
                        </div>
                        <div className="timeline-content">
                          <div className="d-flex align-items-center mb-2">
                            {getStatusBadge(action.status)}
                            <span className="text-soft ms-2">
                              par {action.user ? `${action.user.first_name || ""} ${action.user.last_name || ""}`.trim() || action.user.email : "Système"}
                            </span>
                          </div>
                          {action.action && (
                            <div className="mb-2">
                              <strong>Action:</strong> {action.action}
                            </div>
                          )}
                          {action.comments && (
                            <div className="mb-2">
                              <strong>Commentaires:</strong> {action.comments}
                            </div>
                          )}
                          {action.due_date && (
                            <div className="text-soft">
                              <Icon name="calender-date" className="me-1"></Icon>
                              Échéance: {new Date(action.due_date).toLocaleDateString("fr-FR")}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <span className="text-soft">Aucun historique disponible</span>
                  </div>
                )}
                <div className="text-end mt-4">
                  <Button color="light" onClick={() => setHistoryModal(false)}>
                    Fermer
                  </Button>
                </div>
              </div>
            )}
          </ModalBody>
        </Modal>

        {/* Modal pour assigner */}
        <Modal isOpen={assignModal} toggle={() => setAssignModal(false)} className="modal-dialog-centered">
          <div className="modal-header">
            <h5 className="modal-title">Assigner l'observation</h5>
            <button className="close" onClick={() => setAssignModal(false)}>
              <Icon name="cross"></Icon>
            </button>
          </div>
          <ModalBody>
            {selectedObservation && (
              <div>
                <div className="form-group mb-3">
                  <Label>Observation</Label>
                  <div className="form-control-plaintext">{selectedObservation.title}</div>
                </div>
                <div className="form-group mb-3">
                  <Label>Assigner à <span className="text-danger">*</span></Label>
                  <RSelect
                    options={userSelectOptions}
                    value={selectedAssigneeOption(statusFormData.assigned_to)}
                    onChange={(opt) =>
                      setStatusFormData({
                        ...statusFormData,
                        assigned_to: opt ? opt.value : "",
                      })
                    }
                    placeholder="Choisir un utilisateur…"
                    isSearchable
                  />
                </div>
                <div className="text-end mt-4">
                  <Button color="light" onClick={() => setAssignModal(false)} className="me-2">
                    Annuler
                  </Button>
                  <Button color="primary" onClick={handleAssign}>
                    Assigner
                  </Button>
                </div>
              </div>
            )}
          </ModalBody>
        </Modal>

        {/* Modal pour actions en masse */}
        <Modal isOpen={bulkActionModal} toggle={() => setBulkActionModal(false)} className="modal-dialog-centered">
          <div className="modal-header">
            <h5 className="modal-title">Actions en masse ({selectedItems.length} observation(s))</h5>
            <button className="close" onClick={() => setBulkActionModal(false)}>
              <Icon name="cross"></Icon>
            </button>
          </div>
          <ModalBody>
            <div className="form-group mb-3">
              <Label>Nouveau statut <span className="text-danger">*</span></Label>
              <RSelect
                options={statusOptions}
                value={statusOptions.find((opt) => opt.value === statusFormData.status) || null}
                onChange={(e) => setStatusFormData({ ...statusFormData, status: e.value })}
              />
            </div>
            <div className="form-group mb-3">
              <Label>Commentaires</Label>
              <Input
                type="textarea"
                rows="3"
                value={statusFormData.comments}
                onChange={(e) => setStatusFormData({ ...statusFormData, comments: e.target.value })}
                placeholder="Commentaires pour toutes les observations..."
              />
            </div>
            <div className="text-end mt-4">
              <Button color="light" onClick={() => setBulkActionModal(false)} className="me-2">
                Annuler
              </Button>
              <Button color="primary" onClick={handleBulkStatusChange}>
                Appliquer
              </Button>
            </div>
          </ModalBody>
        </Modal>

        {/* Sidebar pour les détails */}
        <div
          className={`nk-sidebar nk-sidebar-right ${detailSidebar ? "sidebar-active" : ""}`}
          style={{
            position: "fixed",
            top: 0,
            right: detailSidebar ? 0 : "-400px",
            width: "400px",
            height: "100vh",
            backgroundColor: "#fff",
            boxShadow: "-2px 0 10px rgba(0,0,0,0.1)",
            transition: "right 0.3s ease",
            zIndex: 1050,
            overflowY: "auto",
          }}
        >
          {selectedObservation && (
            <div className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5>Détails de l'observation</h5>
                <Button
                  color="light"
                  size="sm"
                  onClick={() => {
                    setDetailSidebar(false);
                    setSelectedObservation(null);
                  }}
                >
                  <Icon name="cross"></Icon>
                </Button>
              </div>
              <div className="mb-3">
                <Label className="text-soft">Titre</Label>
                <div className="form-control-plaintext">{selectedObservation.title}</div>
              </div>
              <div className="mb-3">
                <Label className="text-soft">Type</Label>
                <div>{getTypeBadge(selectedObservation.type)}</div>
              </div>
              <div className="mb-3">
                <Label className="text-soft">Statut</Label>
                <div>{getStatusBadge(selectedObservation.status)}</div>
              </div>
              {selectedObservation.severity && (
                <div className="mb-3">
                  <Label className="text-soft">Gravité</Label>
                  <div>
                    <Badge color={selectedObservation.severity === "high" ? "danger" : selectedObservation.severity === "medium" ? "warning" : "success"}>
                      {selectedObservation.severity === "high" ? "Élevée" : selectedObservation.severity === "medium" ? "Moyenne" : "Faible"}
                    </Badge>
                  </div>
                </div>
              )}
              <div className="mb-3">
                <Label className="text-soft">Description</Label>
                <div className="form-control-plaintext" style={{ whiteSpace: "pre-wrap" }}>
                  {selectedObservation.description || "-"}
                </div>
              </div>
              {selectedObservation.site && (
                <div className="mb-3">
                  <Label className="text-soft">Site</Label>
                  <div className="form-control-plaintext">{selectedObservation.site.name}</div>
                </div>
              )}
              <div className="mb-3">
                <Label className="text-soft">Assigné à</Label>
                <div className="form-control-plaintext">
                  {selectedObservation.assignee
                    ? `${selectedObservation.assignee.first_name || ""} ${selectedObservation.assignee.last_name || ""}`.trim() || selectedObservation.assignee.email
                    : "Non assigné"}
                </div>
              </div>
              <div className="mb-3">
                <Label className="text-soft">Date de création</Label>
                <div className="form-control-plaintext">
                  {selectedObservation.created_at ? new Date(selectedObservation.created_at).toLocaleString("fr-FR") : "-"}
                </div>
              </div>
              <div className="mt-4 d-flex g-2">
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => {
                    setDetailSidebar(false);
                    openStatusModal(selectedObservation);
                  }}
                  className="me-2"
                >
                  <Icon name="edit"></Icon>
                  <span>Changer le statut</span>
                </Button>
                <Button
                  color="secondary"
                  size="sm"
                  onClick={() => {
                    setDetailSidebar(false);
                    openHistoryModal(selectedObservation);
                  }}
                >
                  <Icon name="history"></Icon>
                  <span>Historique</span>
                </Button>
              </div>
            </div>
          )}
        </div>
        {detailSidebar && (
          <div
            className="sidebar-overlay"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0,0,0,0.5)",
              zIndex: 1040,
            }}
            onClick={() => {
              setDetailSidebar(false);
              setSelectedObservation(null);
            }}
          ></div>
        )}
      </Content>
    </React.Fragment>
  );
};

export default WorkflowListPage;
